import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Bell, BellRing, X, Check, CheckCheck, 
  MessageSquare, Calendar, TrendingUp, Store,
  Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Hook para gestionar conexión WebSocket con el servidor
 */
export const useWebSocket = (adminId, enabled = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Audio para notificaciones
  const notificationSound = useRef(null);
  
  useEffect(() => {
    // Crear elemento de audio para notificaciones
    notificationSound.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleAYAQKvg8teleAYAQKvg8sF/UwwAh9j1y5xsHAButu32tntJEgBatPDzrXE/CQBOqu/0qWw5BgBFnu30pGg1BAA9k+30oGQyAgA2iO30nWEvAQAwhO30mmAuAQArhO30mF8tAQAmguz0lV0sAQAigOz0k1wqAQAegOz0kVspAQAbfuz0kFonAQAYfOz0j1kmAAEVeuz0jlglAAERd+z0jVckAAEOduz0jFYjAAELc+z0i1UiAAEJcuz0ilQhAAEHcOz0iVMgAAEFbuz0iFIfAAEDbez0h1EeAAEBa+z0hlAcAAD/aez0hU8bAAD9Z+z0hE4aAAD7Zuz0g00ZAAD5ZOz0gkwYAAD3Yuz0gUsXAAD1YOz0gEoWAAD0X+z0f0kVAADyXez0fkgUAADwW+z0fUcTAADuWuz0fEYSAADsWOz0e0URAAD');
  }, []);
  
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && notificationSound.current) {
      notificationSound.current.play().catch(() => {});
    }
  }, [soundEnabled]);
  
  const connect = useCallback(() => {
    if (!adminId || !enabled || wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '').replace('/api', '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/admin/${adminId}`;
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Cancelar cualquier intento de reconexión pendiente
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        // Intentar reconectar después de 5 segundos
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }, [adminId, enabled]);
  
  const handleMessage = useCallback((data) => {
    console.log('WebSocket message:', data);
    
    switch (data.type) {
      case 'connected':
        console.log('Connected to notification service');
        break;
        
      case 'pending_notifications':
        setNotifications(data.data.notifications || []);
        setUnreadCount(data.data.count || 0);
        break;
        
      case 'whatsapp_response':
        // Nueva notificación de WhatsApp
        const waNotification = {
          id: Date.now().toString(),
          type: 'whatsapp_response',
          message: data.data.intent_label || 'Nuevo mensaje de WhatsApp',
          business_name: data.data.business_name,
          business_id: data.data.business_id,
          timestamp: data.timestamp,
          read: false
        };
        
        setNotifications(prev => [waNotification, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        
        // Mostrar toast y reproducir sonido
        if (data.sound) {
          playNotificationSound();
        }
        
        toast.info(
          <div className="flex items-start gap-2">
            <MessageSquare className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{data.data.business_name}</p>
              <p className="text-sm text-gray-400">{data.data.intent_label}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
        break;
        
      case 'consultation_booked':
        // Nueva consultoría agendada
        const consultNotification = {
          id: Date.now().toString(),
          type: 'consultation_booked',
          message: data.data.message,
          business_name: data.data.business_name,
          timestamp: data.timestamp,
          read: false
        };
        
        setNotifications(prev => [consultNotification, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        
        if (data.sound) {
          playNotificationSound();
        }
        
        toast.success(
          <div className="flex items-start gap-2">
            <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{data.data.message}</p>
              <p className="text-sm text-gray-400">{data.data.datetime}</p>
            </div>
          </div>,
          { duration: 8000 }
        );
        break;
        
      case 'potential_calculated':
        const potentialNotification = {
          id: Date.now().toString(),
          type: 'potential_calculated',
          message: data.data.message,
          business_name: data.data.business_name,
          timestamp: data.timestamp,
          read: false
        };
        
        setNotifications(prev => [potentialNotification, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        
        toast.info(
          <div className="flex items-start gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{data.data.message}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
        break;
        
      case 'new_business':
        const businessNotification = {
          id: Date.now().toString(),
          type: 'new_business',
          message: data.data.message,
          business_name: data.data.business_name,
          timestamp: data.timestamp,
          read: false
        };
        
        setNotifications(prev => [businessNotification, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        
        if (data.sound) {
          playNotificationSound();
        }
        
        toast.info(
          <div className="flex items-start gap-2">
            <Store className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{data.data.message}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
        break;
        
      case 'pong':
        // Respuesta a ping, ignorar
        break;
        
      default:
        console.log('Unknown message type:', data.type);
    }
  }, [playNotificationSound]);
  
  const markAsRead = useCallback((notificationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_read',
        notification_id: notificationId
      }));
    }
    
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? {...n, read: true} : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);
  
  const markAllAsRead = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_all_read'
      }));
    }
    
    setNotifications(prev => prev.map(n => ({...n, read: true})));
    setUnreadCount(0);
  }, []);
  
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);
  
  useEffect(() => {
    connect();
    
    // Ping cada 30 segundos para mantener la conexión viva
    const pingInterval = setInterval(sendPing, 30000);
    
    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect, sendPing]);
  
  return {
    isConnected,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    soundEnabled,
    setSoundEnabled
  };
};


/**
 * Componente de Notificaciones para el Header del Admin
 */
export const NotificationBell = ({ adminId }) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  const [isOpen, setIsOpen] = useState(false);
  
  const {
    isConnected,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    soundEnabled,
    setSoundEnabled
  } = useWebSocket(adminId);
  
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'whatsapp_response':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'consultation_booked':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'potential_calculated':
        return <TrendingUp className="w-4 h-4 text-amber-500" />;
      case 'new_business':
        return <Store className="w-4 h-4 text-emerald-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return isSpanish ? 'ahora' : 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };
  
  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          isOpen ? 'bg-gray-700' : 'hover:bg-gray-700'
        }`}
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-emerald-400 animate-pulse" />
        ) : (
          <Bell className="w-5 h-5 text-gray-400" />
        )}
        
        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        
        {/* Connection indicator */}
        <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${
          isConnected ? 'bg-green-500' : 'bg-red-500'
        }`} />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden z-50">
          {/* Header */}
          <div className="p-3 border-b border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-white">
              {isSpanish ? 'Notificaciones' : 'Notifications'}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="p-1 text-gray-400 hover:text-white"
                title={soundEnabled ? 'Mute' : 'Unmute'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-emerald-400 hover:text-emerald-300"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-gray-700 hover:bg-gray-750 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-gray-750/50' : ''
                  }`}
                  onClick={() => {
                    if (!notification.read) markAsRead(notification.id);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${notification.read ? 'text-gray-400' : 'text-white'}`}>
                        {notification.message || notification.business_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(notification.timestamp)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>{isSpanish ? 'Sin notificaciones' : 'No notifications'}</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-2 border-t border-gray-700 text-center">
            <span className={`text-xs ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
              {isConnected 
                ? (isSpanish ? '● Conectado' : '● Connected')
                : (isSpanish ? '○ Desconectado' : '○ Disconnected')
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
